import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { ShippingState } from '../../../store/shipping/shipping.reducer';
import * as ShippingActions from '../../../store/shipping/shipping.actions';
import { BehaviorSubject, Observable } from "rxjs";

@Injectable({
  providedIn: 'root'
})
export class IsDeliveryFreeService {

  private shippingCost: number = 0;
  private distance: number = 0;
  private mountForDelivery = 0;
  private percent: number = 0;
  private totalCost: number = 0;

  // BehaviorSubjects para observar cambios
  private shippingCostSubject = new BehaviorSubject<number>(0);
  private distanceSubject = new BehaviorSubject<number>(0);
  private totalCostSubject = new BehaviorSubject<number>(0);
  private isShippingFreeSubject = new BehaviorSubject<boolean>(false);

  constructor(private store: Store<{ shipping: ShippingState }>) {
    // Cargar valores iniciales del store
    this.store.select(state => state.shipping).subscribe(shipping => {
      this.shippingCost = Number(shipping.shippingCost);
      this.distance = Number(shipping.kmDistance);
      this.mountForDelivery = Number(shipping.mountForDelivery);
      this.percent = Number(shipping.deliveryPercent);
      this.totalCost = Number(shipping.totalCost);

      // Actualizar subjects
      this.shippingCostSubject.next(this.shippingCost);
      this.distanceSubject.next(this.distance);
      this.totalCostSubject.next(this.totalCost);
      this.updateShippingFreeStatus();
    });
  }

  setShippingCost(cost: number): void {
    this.shippingCost = cost;
    this.store.dispatch(ShippingActions.setShippingCost({ cost: cost.toString() }));
    this.shippingCostSubject.next(cost);
    // Actualizar el estado de envío gratis
    this.updateShippingFreeStatus();
  }

  // Method to set distance
  setDistance(km: number): void {
    this.distance = km;
    this.store.dispatch(ShippingActions.setKmDistance({ distance: km.toString() }));
    this.distanceSubject.next(km);
  }

  setMountDeliveryForFree(mount: number | null | undefined): void {
    const safeMount = Number(mount ?? 0);
    this.mountForDelivery = safeMount;
    this.store.dispatch(ShippingActions.setMountForDelivery({ mount: safeMount.toString() }));
  }

  getMountDeliveryForFree(): number {
    return this.mountForDelivery;
  }

  setPercent(percent: number): void {
    this.percent = percent;
    this.store.dispatch(ShippingActions.setDeliveryPercent({ percent: percent.toString() }));
  }

  getPercent(): number | null {
    return this.percent;
  }

  // Evalúa si aplica envío gratis por porcentaje
  private isShippingFreeByPercent(): boolean {
    const currentPercent = this.getPercent();

    // Si es null, no aplica envío gratis
    if (currentPercent === null) {
      return false;
    }

    // Si es 0, sí aplica envío gratis
    if (currentPercent === 0) {
      return true;
    }

    return false;
  }

  // Evalúa si aplica envío gratis por monto mínimo
  private isShippingFreeByAmount(): boolean {
    // console.log('Evaluando monto:', this.mountForDelivery);

    // Si no hay monto válido configurado, no aplica
    if (this.mountForDelivery == null || this.mountForDelivery == 0) {
      return false;
    }

    // Si el total supera el monto requerido o es igual
    return this.totalCost >= this.mountForDelivery;
  }

  // Función principal que combina ambas evaluaciones
  isCostShippingFree(): boolean {
    // sobres si  cualquiera de las dos condiciones es true, el envío es gratis
    return this.isShippingFreeByPercent() || this.isShippingFreeByAmount();
  }

  // Método para actualizar el estado de envío gratis
  private updateShippingFreeStatus(): void {
    const isShippingFree = this.isCostShippingFree();
    this.isShippingFreeSubject.next(isShippingFree);
  }

  setTotalCost(total_cost: number) {
    this.totalCost = total_cost;
    this.store.dispatch(ShippingActions.setTotalCost({ totalCost: total_cost.toString() }));
    this.totalCostSubject.next(total_cost);
    // Actualizar el estado de envío gratis
    this.updateShippingFreeStatus();
  }

  getTotalCost(): number {
    return this.totalCost;
  }

  getShippingCost(): number {
    return this.shippingCost;
  }

  // Observable para el costo de envío
  getShippingCost$(): Observable<number> {
    return this.shippingCostSubject.asObservable();
  }

  // Observable para el total
  getTotalCost$(): Observable<number> {
    return this.totalCostSubject.asObservable();
  }

  // Observable para el estado de envío gratis
  getIsShippingFree$(): Observable<boolean> {
    return this.isShippingFreeSubject.asObservable();
  }

  getDistance(): number {
    return this.distance;
  }

  // Observable para la distancia
  getDistance$(): Observable<number> {
    return this.distanceSubject.asObservable();
  }

}
